/**
 * Profile Lenta Tab — shows user's posts and reposts.
 * Infinite scroll, same PostCard design as main feed.
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useUserPosts, useFollowingSets } from '@turnifly/shared/hooks'
import { PostCard } from '../../feed/components/post-card'
import { PostDetailSheet } from '../../feed/components/post-detail-sheet'
import { RepostSheet } from '../../feed/components/repost-sheet'
import { Skeleton } from '@turnifly/shared/ui'
import type { FeedItem } from '@turnifly/shared/types'

interface ProfileLentaTabProps {
  userId: string
  highlightPostId?: string
  onHighlightHandled?: () => void
}

export function ProfileLentaTab({ userId, highlightPostId, onHighlightHandled }: ProfileLentaTabProps): React.ReactElement {
  const { t } = useTranslation()
  const { user: authUser } = useAuthStore()
  const currentUserId = authUser?.id

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useUserPosts(userId, currentUserId)
  const { data: followSets } = useFollowingSets(currentUserId || undefined)

  const [detailPost, setDetailPost] = useState<{ id: string; type: string } | null>(null)
  const [repostTarget, setRepostTarget] = useState<FeedItem | null>(null)

  const allPosts = useMemo<FeedItem[]>(
    () => data?.pages.flat() ?? [],
    [data],
  )

  const acceptedSet = followSets?.accepted ?? new Set<string>()

  // Deep link: open specific post's comment sheet when data loads
  const highlightHandled = useRef(false)
  useEffect(() => {
    if (highlightPostId && allPosts.length > 0 && !highlightHandled.current) {
      highlightHandled.current = true
      const post = allPosts.find((p) => p.post_id === highlightPostId)
      if (post) {
        setDetailPost({ id: highlightPostId, type: post.post_type })
      }
      onHighlightHandled?.()
    }
  }, [highlightPostId, allPosts, onHighlightHandled])

  const handleCommentPress = useCallback((postId: string): void => {
    const post = allPosts.find((p) => p.post_id === postId)
    setDetailPost(post ? { id: postId, type: post.post_type } : null)
  }, [allPosts])

  const handleRepostPress = useCallback((postId: string): void => {
    const post = allPosts.find((p) => p.post_id === postId)
    if (post) setRepostTarget(post)
  }, [allPosts])

  const handleLoadMore = useCallback((): void => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="w-10 h-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (allPosts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FileText size={32} className="text-text-hint/15 mb-3" />
        <p className="text-[14px] text-text-hint/30">{t('profile.noPostsYet')}</p>
      </div>
    )
  }

  return (
    <>
      <div className="-mx-4">
        {allPosts.map((item) => (
          <PostCard
            key={item.post_id}
            item={item}
            followState={item.author_id === currentUserId ? 'following' : acceptedSet.has(item.author_id) ? 'following' : 'none'}
            onCommentPress={handleCommentPress}
            onFollowPress={() => {}}
            onRepostPress={handleRepostPress}
          />
        ))}

        {/* Load more */}
        {hasNextPage && (
          <div className="flex justify-center py-4">
            <button
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              className="text-[13px] text-primary font-medium tap-highlight-transparent"
            >
              {isFetchingNextPage
                ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                : t('feed.loadMore')
              }
            </button>
          </div>
        )}
      </div>

      {/* Repost sheet */}
      <RepostSheet post={repostTarget} onClose={() => setRepostTarget(null)} />

      {/* Comment sheet */}
      {detailPost && (
        <PostDetailSheet
          postId={detailPost.id}
          postType={detailPost.type}
          onClose={() => setDetailPost(null)}
        />
      )}
    </>
  )
}
